import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { MenuItems } from '../../enums/MenuItems'
import LayoutSplitView from '../../components/layout/LayoutSplitView'
import { PersonDto, PersonLiteDto } from '../../api/person/models/PersonDto'
import personApi from '../../api/person/PersonApi'
import roleApi from '../../api/role/RoleApi'
import AddNetworkLinksModal from '../../components/AddNetworkLinksModal'
import { parse } from 'lucene-query-parser'
import {
    HelixSearchV2Summary,
    StartHelixSearchV2Req,
    StartHelixSearchV2Res,
    HelixBooleanSearchEvalParams,
} from '../../api/helixSearchV2/models/StartHelixSearchV2'
import { GetHelixSearchStatusRes } from '../../api/helixSearchV2/models/StartHelixSearchV2'
import {
    CandidateLiteDto,
    GenericCandidateLite,
    HelixCandidateLiteDto,
    PresentCandidateValues,
} from '../../api/role/models/CandidateDto'
import { MapExtra } from '../../utils/MapExtra'
import networkApi from '../../api/network/NetworkApi'
import {
    ConnectionDetailLiteDto,
    ResolvedPersonLiteDto,
} from '../../api/network/models/getNetworkScopedDtos'
import Note from '../../components/Note'
import { NoteType } from '../../enums/NoteType'
import { CompanyDto } from '../../api/company/models/CompanyDto'
import companyApi from '../../api/company/CompanyApi'
import { HelixSearchV2ResultDto } from '../../api/helixSearchV2/models/HelixSearchV2Results'
import HelixBooleanSearchHelp from '../../components/helixSearch/rightPanes/HelixBooleanSearchHelp'
import CandidateRightPane from '../../components/CandidateRightPane'
import { RoleLiteDto } from '../../api/role/models/RoleDto'
import {
    convertMinRelationshipScoreToNumber,
    convertOrderByToV2SortBy,
    convertShortlistedSelectedToV2FilterValue,
} from '../../utils/helixSearchV2Utils'
import { useManageCandidateStore } from '../../components/manageRole/stateStore/manageCandidateStore'
import { toast } from 'sonner'
import {
    buildCandidatePresentMap,
    getCandidatesNeedingSave,
} from '../../utils/candidateUtils'
import helixSearchV2Api from '../../api/helixSearchV2/HelixSearchV2Api'
import PersonNetworkImportModal, {
    type NetworkImportModalState,
} from '../person/import/PersonNetworkImportModal'
import CandidateList from './CandidateList'
import SearchFilters from './searchFilters/SearchFilters'
import {
    applyHelixSearchFilters,
    buildBooleanQueryFromFilters,
    buildHelixSearchFiltersPayload,
    createDefaultFilterInputs,
    type ExperienceBounds,
    type FilterInputs,
    type SearchInvocationOptions,
} from './searchFilters/filterTypes'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faChartBar,
    faFilter,
    faHexagonNodes,
    faPencil,
    faBookmark,
} from '@fortawesome/free-solid-svg-icons'
import HelixSearchV2Filters from './HelixSearchV2Filters'
import HelixSearchV2Insights from './HelixSearchV2Insights'
import HelixSearchNetworkView from '../../components/helixSearch/HelixSearchNetworkView'
import HelixSearchDataLoadingModal from '../../components/helixSearch/HelixSearchDataLoadingModal'
import HelixSearchV2ProgressModalCopy from './HelixSearchV2ProgressModalCopy'
import {
    setStateIfChanged,
    setStringArrayStateIfChanged,
} from '../../utils/stateUtils'
import RoleCard from '../role/RoleCard'
import Button from '../common/form/inputs/Button'
import { Pages } from '../../Pages'
import CtxtLink from '../common/links/CtxtLink'
import type { GetRoleLiteRes } from '../../api/role/models/getLite/GetRoleLiteRes'

type FilterData = {
    currentPageNumber: number
    perPage: number
    networkLinksSelected: string[]
    minRelationshipScore: string
    mustBeInBooleanSearchResults: boolean
    mustHaveRelationship: boolean
    shortlistedSelected: string[]
    orderBy: string
}

const HELIX_PROGRESS_MODAL_AUTO_CLOSE_DELAY_MS = 0
const EMPTY_CONNECTION_DETAILS: ConnectionDetailLiteDto[] = []
const DEFAULT_MIN_EXPERIENCE_YEARS = 0
const DEFAULT_MAX_EXPERIENCE_YEARS = 20

function getDefaultOrderBy(llmScoringEnabled: boolean): string {
    return llmScoringEnabled ? 'llmRelevanceScore_desc' : 'combined_desc'
}

function buildNetworkPersonLabel(person: PersonLiteDto): string {
    const nameParts = [person.firstName, person.lastName].filter(Boolean)
    const name = nameParts.join(' ').trim()

    const rawJobTitle = person.currentJobDetails?.workedAt?.jobTitle
    const jobTitle = rawJobTitle ? rawJobTitle.trim() : undefined

    const rawCompanyName =
        person.currentJobDetails?.company?.name ??
        person.currentJobDetails?.workedAt?.company?.name
    const companyName = rawCompanyName ? rawCompanyName.trim() : undefined

    if (jobTitle && companyName) {
        return `${
            name || 'Trusted Circle contact'
        } • ${jobTitle} @ ${companyName}`
    }

    if (jobTitle) {
        return `${name || 'Trusted Circle contact'} • ${jobTitle}`
    }

    if (companyName) {
        return `${name || 'Trusted Circle contact'} • ${companyName}`
    }

    return name || 'Trusted Circle contact'
}

export default function HelixSearchV2Copy(): React.JSX.Element {
    const location = useLocation()
    const urlParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search],
    )
    const shouldAutoOpenFilters = useMemo(() => {
        const value = urlParams.get('autoOpenFilters')
        if (value === null) {
            return false
        }
        const normalized = value.trim().toLowerCase()
        if (normalized === 'true') {
            return true
        }
        return false
    }, [urlParams])
    const [roleId, setRoleId] = useState<string | null>(null)
    const roleIdRef = useRef<string | null>(null)
    const [roleLite, setRoleLite] = useState<RoleLiteDto | null>(null)
    const navigate = useNavigate()
    const encodedReturnToEditRole = useMemo(
        () => encodeURIComponent(`${location.pathname}${location.search}`),
        [location.pathname, location.search],
    )

    const insightsRef = useRef<HTMLDivElement>(null)
    const networkViewRef = useRef<HTMLDivElement | null>(null)
    const personLiteCacheRef = useRef(new MapExtra<string, PersonLiteDto>())
    const personCacheRef = useRef(new MapExtra<string, PersonDto>())
    const companyCacheRef = useRef(new MapExtra<string, CompanyDto>())
    const [networkImportModalState, setNetworkImportModalState] =
        useState<NetworkImportModalState | null>(null)
    const networkImportPromiseRef = useRef<Promise<void> | null>(null)
    const networkImportPromiseResolveRef = useRef<(() => void) | null>(null)
    const networkImportPromiseRejectRef = useRef<
        ((error?: Error) => void) | null
    >(null)
    const helixSearchPollAbortRef = useRef<(() => void) | null>(null)
    const autoOpenFiltersConsumedRef = useRef(false)
    const searchRef = useRef<
        (options?: {
            overrideQuery?: string
            enableLlmScoring?: boolean
            onError?: (message: string) => void
            networkSeedPersonIds?: string[]
        }) => Promise<void>
    >(async () => {})
    const autoRestartSstRef = useRef<string | null>(null)
    const hydratedFiltersSstRef = useRef<string | null>(null)
    const appliedRoleFiltersRef = useRef<string | null>(null)
    const [roleFiltersRefreshSignal, setRoleFiltersRefreshSignal] = useState(0)
    const roleDescriptionForFiltersRef = useRef<string | null>(null)
    const [resolvedPersonLites, setResolvedPersonLites] = useState<
        ResolvedPersonLiteDto[]
    >([])
    const [filters, setFilters] = useState<FilterInputs>(() =>
        createDefaultFilterInputs([]),
    )
    const filtersRef = useRef(filters)

    useEffect(() => {
        filtersRef.current = filters
    }, [filters])

    useEffect(() => {
        if (!shouldAutoOpenFilters) {
            autoOpenFiltersConsumedRef.current = false
            return
        }
        if (autoOpenFiltersConsumedRef.current) {
            return
        }
        autoOpenFiltersConsumedRef.current = true
        setFiltersModalOpenSignal((prev) => prev + 1)
        const nextParams = new URLSearchParams(location.search)
        if (!nextParams.has('autoOpenFilters')) {
            return
        }
        nextParams.delete('autoOpenFilters')
        const nextSearch = nextParams.toString()
        const nextUrl =
            nextSearch.length > 0
                ? `${location.pathname}?${nextSearch}`
                : location.pathname
        navigate(nextUrl, { replace: true })
    }, [shouldAutoOpenFilters, location.pathname, location.search, navigate])

    const [query, setQuery] = React.useState<string>(`developer OR engineer`)
    const [, setBooleanQueryError] = useState<string | null>(null)
    const [trustedCircleError, setTrustedCircleError] = useState<string | null>(
        null,
    )
    const [filtersModalSearchError, setFiltersModalSearchError] = useState<
        string | null
    >(null)
    const ignoreRetiredPeople = true
    const [minExperienceYears, setMinExperienceYears] = useState<number>(
        DEFAULT_MIN_EXPERIENCE_YEARS,
    )
    const [maxExperienceYears, setMaxExperienceYears] = useState<number>(
        DEFAULT_MAX_EXPERIENCE_YEARS,
    )
    const [enabledLLMScoring, setEnabledLLMScoring] =
        React.useState<boolean>(false)
    const [showResults, setShowResults] = React.useState<boolean>(false)
    const [initialRoleLoadComplete, setInitialRoleLoadComplete] =
        useState<boolean>(false)

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [filtersModalOpenSignal, setFiltersModalOpenSignal] = useState(0)
    const [
        networkLinksIdsSelectedInNetworkLinks,
        setNetworkLinksIdsSelectedInNetworkLinks,
    ] = useState<string[]>([])
    const [
        networkLinksSelectedInNetworkLinks,
        setNetworkLinksSelectedInNetworkLinks,
    ] = useState<PersonLiteDto[]>([])
    const hasOpenedTrustedCircleModalRef = useRef<boolean>(false)
    const networkPersonNamesById = useMemo(() => {
        const labels: Record<string, string> = {}
        for (const person of networkLinksSelectedInNetworkLinks) {
            labels[person.id] = buildNetworkPersonLabel(person)
        }
        return labels
    }, [networkLinksSelectedInNetworkLinks])
    const networkPersonPhotoUrlsById = useMemo(() => {
        const photos: Record<string, string> = {}
        for (const person of networkLinksSelectedInNetworkLinks) {
            if (person.linkedInProfilePicUrl) {
                photos[person.id] = person.linkedInProfilePicUrl
            }
        }
        return photos
    }, [networkLinksSelectedInNetworkLinks])
    // used by the filters to show the available network links
    //  only set once a search is initiated
    const [networkLinkIdsSetInThisSearch, setNetworkLinkIdsSetInThisSearch] =
        useState<string[]>([])
    const [helixV2SearchSummary, setHelixV2SearchSummary] =
        useState<HelixSearchV2Summary | null>(null)
    const [isHelixV2SearchComplete, setIsHelixV2SearchComplete] =
        useState(false)
    const [showHelixV2ProgressModal, setShowHelixV2ProgressModal] =
        useState(false)
    const [helixV2StatusDetails, setHelixV2StatusDetails] =
        useState<GetHelixSearchStatusRes | null>(null)
    const [progressModalAutoCloseDeadline, setProgressModalAutoCloseDeadline] =
        useState<number | null>(null)
    const progressModalAutoCloseTimeoutRef = useRef<number | null>(null)
    const pageLoadModalOpenRef = useRef<boolean>(false)
    const experienceOverrideRef = useRef<ExperienceBounds | null>(null)

    const [errorMsg, setErrorMsg] = useState<string | null>(null)

    const [searchResults, setSearchResults] = useState<
        HelixSearchV2ResultDto[]
    >([])
    const [rightPaneMode, setRightPaneMode] = useState<
        | 'search_filters'
        | 'boolean_search_help'
        | 'saved_searches'
        | 'candidate'
        | 'network_seed'
        | 'company'
    >('search_filters')
    const [selectedCandidateId, setSelectedCandidateId] = useState<
        string | null
    >(null)
    const selectedCandidateIdRef = useRef<string | null>(null)
    const [helixCandidateLiteDtos, setHelixCandidateLiteDtos] = useState<
        HelixCandidateLiteDto[]
    >([])

    // ##### filter state - start #####
    // const filtersRef = useRef(() => getCurrentFilters())
    const [networkLinksSelectedInFilters, setNetworkLinksSelectedInFilters] =
        useState<string[]>([])
    const [shortlistedSelected, setShortlistedSelected] = useState<string[]>([
        'blanks',
        'yes',
    ])
    const [minRelationshipScore, setMinRelationshipScore] = useState<string>(
        'min_relationship_score_20',
    )
    const [orderBy, setOrderBy] = useState<string>(getDefaultOrderBy(false))
    const [mustBeInBooleanSearchResults, setMustBeInBooleanSearchResults] =
        useState<boolean>(true)
    const [mustHaveRelationship, setMustHaveRelationship] =
        useState<boolean>(true)
    const [filteredCandidatePoolCount, setFilteredCandidatePoolCount] =
        useState<number>(0)
    const [
        filteredCandidatePoolCountIsLoading,
        setFilteredCandidatePoolCountIsLoading,
    ] = useState<boolean>(false)
    const [filteredTopPickCount, setFilteredTopPickCount] = useState<number>(0)
    const [filtersAreDirty, setFiltersAreDirty] = useState<boolean>(false)
    const [isSavingFilters, setIsSavingFilters] = useState<boolean>(false)
    const [currentPageNumber, setCurrentPageNumber] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(50)
    const [totalResultCount, setTotalResultCount] = useState<number>(0)
    const [showDataLoading, setShowDataLoading] = useState<boolean>(false)
    const [resultsEnableLlmScoring, setResultsEnableLlmScoring] =
        useState<boolean>(false)
    const [isSavingAllCandidates, setIsSavingAllCandidates] =
        useState<boolean>(false)
    // ##### filter state - end #####

    const manageCandidateStore = useManageCandidateStore()
    const setStoreCandidates = useManageCandidateStore((s) => s.setCandidates)
    const setEphemeralCandidates = useManageCandidateStore(
        (s) => s.setEphemeralCandidates,
    )
    const setRoleLiteInStore = useManageCandidateStore((s) => s.setRoleLite)
    const [roleCandidatesLoaded, setRoleCandidatesLoaded] = useState(false)
    const [initialSavedCandidateCount, setInitialSavedCandidateCount] =
        useState<number | null>(null)
    const savedCandidateCount = useManageCandidateStore((s) =>
        s.candidates.reduce(
            (count, candidate) =>
                count +
                (candidate.present === PresentCandidateValues.YES ? 1 : 0),
            0,
        ),
    )
    const storeCandidates = useManageCandidateStore((s) => s.candidates)
    const storeEphemeralCandidates = useManageCandidateStore(
        (s) => s.ephemeralCandidates,
    )
    const candidatePresentById = useMemo(
        () =>
            buildCandidatePresentMap(storeCandidates, storeEphemeralCandidates),
        [storeCandidates, storeEphemeralCandidates],
    )

    // ############## NEW CONTROL FLOW - start ##############

    useEffect(() => {
        roleIdRef.current = roleId
    }, [roleId])

    // WARNING: This useEffect that sets the filterRef MUST MUST MUST live at the top!
    //  React will fire useEffects in the order they are declared and other useEffects rely on the filterRef
    //  being up to date before they run. This is pretty horrible I know but it works.
    // this is a great react trick! - start
    // we use a ref to store the current filter state
    // and a useEffect to update it whenever the filter state changes
    // then functions and components can access the current filter state without needing to depend on the
    //  useState variables. It avoids things being re-declared which triggers re-renders etc...
    const filterRef = useRef<FilterData>({
        currentPageNumber,
        perPage,
        networkLinksSelected: networkLinksSelectedInFilters,
        minRelationshipScore,
        mustBeInBooleanSearchResults,
        mustHaveRelationship,
        shortlistedSelected,
        orderBy,
    })

    useEffect(() => {
        filterRef.current = {
            currentPageNumber,
            perPage,
            networkLinksSelected: networkLinksSelectedInFilters,
            minRelationshipScore,
            mustBeInBooleanSearchResults,
            mustHaveRelationship,
            shortlistedSelected,
            orderBy,
        }
    }, [
        currentPageNumber,
        perPage,
        networkLinksSelectedInFilters,
        minRelationshipScore,
        mustBeInBooleanSearchResults,
        mustHaveRelationship,
        shortlistedSelected,
        orderBy,
    ])
    // this is a great react trick! - end

    useEffect(() => {
        if (!roleId) {
            setInitialSavedCandidateCount(null)
            setRoleCandidatesLoaded(false)
            return
        }

        let isMounted = true
        setInitialSavedCandidateCount(null)
        setRoleCandidatesLoaded(false)
        ;(async () => {
            try {
                const candidateCount = await roleApi.getCandidateCountForRole({
                    roleId,
                    shortlisted: PresentCandidateValues.YES,
                })
                if (isMounted) {
                    setInitialSavedCandidateCount(candidateCount)
                }
            } catch (error) {
                console.error(
                    'Failed to fetch saved candidate count for role',
                    error,
                )
            }
        })()

        return () => {
            isMounted = false
        }
    }, [roleId])

    const resetFilters = useCallback(
        (
            selectedNetworkLinkIds: string[],
            llmScoringEnabled: boolean = false,
        ): FilterData => {
            const defaultOrderBy = getDefaultOrderBy(llmScoringEnabled)
            setCurrentPageNumber(1)
            setNetworkLinksSelectedInFilters(selectedNetworkLinkIds)
            setShortlistedSelected(['blanks', 'yes'])
            setMinRelationshipScore('min_relationship_score_20')
            setOrderBy(defaultOrderBy)
            setMustBeInBooleanSearchResults(true)
            setMustHaveRelationship(true)
            setFiltersAreDirty(false)

            return {
                currentPageNumber: 1,
                perPage,
                networkLinksSelected: selectedNetworkLinkIds,
                minRelationshipScore: 'min_relationship_score_20',
                mustBeInBooleanSearchResults: true,
                mustHaveRelationship: true,
                shortlistedSelected: ['blanks', 'yes'],
                orderBy: defaultOrderBy,
            }
        },
        [
            setCurrentPageNumber,
            setNetworkLinksSelectedInFilters,
            setShortlistedSelected,
            setMinRelationshipScore,
            setOrderBy,
            setMustBeInBooleanSearchResults,
            setMustHaveRelationship,
            setFiltersAreDirty,
            perPage,
        ],
    )

    const clampExperienceYears = useCallback((value: number): number => {
        return Math.min(
            DEFAULT_MAX_EXPERIENCE_YEARS,
            Math.max(DEFAULT_MIN_EXPERIENCE_YEARS, Math.round(value)),
        )
    }, [])

    const handleMinExperienceYearsChange = useCallback(
        (value: number) => {
            const clamped = clampExperienceYears(value)
            setMinExperienceYears(clamped)
            setMaxExperienceYears((prev) => (prev < clamped ? clamped : prev))
        },
        [clampExperienceYears],
    )

    const handleMaxExperienceYearsChange = useCallback(
        (value: number) => {
            const clamped = clampExperienceYears(value)
            setMaxExperienceYears(clamped)
            setMinExperienceYears((prev) => (prev > clamped ? clamped : prev))
        },
        [clampExperienceYears],
    )

    const applyHelixStatusUpdate = useCallback(
        (
            status: GetHelixSearchStatusRes,
            options?: { resetFilters?: boolean; targetFilters?: FilterData },
        ) => {
            const {
                sst,
                logId,
                sessionStatus,
                networkSeedPersonIds,
                booleanSearchQuery,
                enableLlmScoring,
                booleanSearchExperienceMonthsMin,
                booleanSearchExperienceMonthsMax,
            } = status

            setHelixV2StatusDetails(status)
            setHelixV2SearchSummary((prev) =>
                prev?.sst === sst && prev?.logId === logId
                    ? prev
                    : { sst, logId },
            )
            setIsHelixV2SearchComplete((prev) => {
                const isComplete = sessionStatus === 'COMPLETE'
                return prev === isComplete ? prev : isComplete
            })

            const uniqueSeedIds = Array.from(
                new Set(networkSeedPersonIds ?? []),
            )
            setStringArrayStateIfChanged(
                setNetworkLinksIdsSelectedInNetworkLinks,
                uniqueSeedIds,
            )
            setStringArrayStateIfChanged(
                setNetworkLinkIdsSetInThisSearch,
                uniqueSeedIds,
            )

            if (hydratedFiltersSstRef.current !== sst) {
                const baseFilters = createDefaultFilterInputs(uniqueSeedIds)
                const mergedFilters = applyHelixSearchFilters(
                    baseFilters,
                    status.searchFilters,
                )
                setFilters(mergedFilters)
                hydratedFiltersSstRef.current = sst
            }

            const nextQuery = booleanSearchQuery ?? ''
            const nextLlmEnabled = enableLlmScoring ?? false
            const nextMinExperienceYears =
                booleanSearchExperienceMonthsMin == null
                    ? DEFAULT_MIN_EXPERIENCE_YEARS
                    : clampExperienceYears(
                          Math.floor(booleanSearchExperienceMonthsMin / 12),
                      )
            const nextMaxExperienceYears =
                booleanSearchExperienceMonthsMax == null
                    ? DEFAULT_MAX_EXPERIENCE_YEARS
                    : clampExperienceYears(
                          Math.ceil(booleanSearchExperienceMonthsMax / 12),
                      )
            const safeMinExperienceYears = Math.min(
                nextMinExperienceYears,
                nextMaxExperienceYears,
            )
            const safeMaxExperienceYears = Math.max(
                nextMinExperienceYears,
                nextMaxExperienceYears,
            )

            setStateIfChanged(setQuery, nextQuery)
            setStateIfChanged(setEnabledLLMScoring, nextLlmEnabled)
            setStateIfChanged(setMinExperienceYears, safeMinExperienceYears)
            setStateIfChanged(setMaxExperienceYears, safeMaxExperienceYears)
            setStateIfChanged(setResultsEnableLlmScoring, nextLlmEnabled)

            if (options?.resetFilters) {
                resetFilters(uniqueSeedIds, nextLlmEnabled)
                if (options.targetFilters) {
                    options.targetFilters.orderBy =
                        getDefaultOrderBy(nextLlmEnabled)
                }
            }
            if (options?.targetFilters) {
                options.targetFilters.networkLinksSelected = uniqueSeedIds
            }
        },
        [
            clampExperienceYears,
            resetFilters,
            setNetworkLinksIdsSelectedInNetworkLinks,
            setNetworkLinkIdsSetInThisSearch,
            setQuery,
            setEnabledLLMScoring,
            setMinExperienceYears,
            setMaxExperienceYears,
            setResultsEnableLlmScoring,
        ],
    )

    useEffect(() => {
        const helixV2Sst = urlParams.get('helixV2Sst')
        if (!helixV2Sst) {
            return
        }
        if (helixV2SearchSummary?.sst === helixV2Sst) {
            return
        }

        hydratedFiltersSstRef.current = null
        let isMounted = true
        ;(async () => {
            try {
                setShowHelixV2ProgressModal(false)
                setIsHelixV2SearchComplete(false)
                pageLoadModalOpenRef.current = true

                const status = await helixSearchV2Api.getSearchStatus({
                    sst: helixV2Sst,
                })
                if (!isMounted) {
                    return
                }
                applyHelixStatusUpdate(status)
                if (status.sessionStatus !== 'EXPIRED') {
                    setShowHelixV2ProgressModal(true)
                }
            } catch (error) {
                console.error(
                    'Failed to load Helix V2 status for existing session',
                    error,
                )
                setErrorMsg(
                    'Unable to check the status of your previous search. Please try again.',
                )
                setShowHelixV2ProgressModal(false)
                pageLoadModalOpenRef.current = false
            }
        })()

        return () => {
            isMounted = false
        }
    }, [applyHelixStatusUpdate, helixV2SearchSummary, urlParams])

    useEffect(() => {
        if (roleFiltersRefreshSignal === 0) {
            return
        }
        appliedRoleFiltersRef.current = null
    }, [roleFiltersRefreshSignal])

    useEffect(() => {
        if (!roleId) {
            appliedRoleFiltersRef.current = null
            return
        }
        if (urlParams.get('helixV2Sst') || helixV2SearchSummary) {
            appliedRoleFiltersRef.current = roleId
            return
        }
        if (appliedRoleFiltersRef.current === roleId) {
            return
        }
        let isMounted = true
        ;(async () => {
            try {
                const res = await helixSearchV2Api.getSessionsForRole({
                    roleId,
                })
                if (!isMounted) {
                    return
                }
                const summary = res.sessionSummaries?.[0]
                appliedRoleFiltersRef.current = roleId
                if (!summary) {
                    return
                }
                const seedIds = summary.networkSeedPersonIds ?? []
                setNetworkLinksIdsSelectedInNetworkLinks(seedIds)
                setNetworkLinkIdsSetInThisSearch(seedIds)
                setNetworkLinksSelectedInFilters(seedIds)
                const baseFilters = createDefaultFilterInputs(seedIds)
                const mergedFilters = applyHelixSearchFilters(
                    baseFilters,
                    summary.searchFilters,
                )
                filtersRef.current = mergedFilters
                setFilters(mergedFilters)
                if (summary.booleanSearchQuery) {
                    setQuery(summary.booleanSearchQuery)
                }
                if (summary.experienceMinYears != null) {
                    setMinExperienceYears(summary.experienceMinYears)
                }
                if (summary.experienceMaxYears != null) {
                    setMaxExperienceYears(summary.experienceMaxYears)
                }
                const llmEnabled = summary.enableLlmScoring ?? false
                setEnabledLLMScoring(llmEnabled)
                setResultsEnableLlmScoring(llmEnabled)
            } catch (error) {
                console.error(
                    'Failed to load latest Helix filters for role',
                    error,
                )
            }
        })()

        return () => {
            isMounted = false
        }
    }, [
        roleId,
        helixV2SearchSummary,
        urlParams,
        roleFiltersRefreshSignal,
        setNetworkLinksIdsSelectedInNetworkLinks,
        setNetworkLinkIdsSetInThisSearch,
        setNetworkLinksSelectedInFilters,
        setFilters,
        setQuery,
        setEnabledLLMScoring,
        setResultsEnableLlmScoring,
        setMinExperienceYears,
        setMaxExperienceYears,
        filtersRef,
    ])

    const loadResults = useCallback(
        async (
            helixV2Summary: HelixSearchV2Summary | null,
            filters: FilterData,
            showLoading: boolean = false,
            bypassCompletionCheck?: boolean,
        ) => {
            const currentRoleId = roleIdRef.current
            const shouldRequireCompletion = !bypassCompletionCheck
            if (showLoading) {
                setShowDataLoading(true)
            }

            if (
                !helixV2Summary ||
                (shouldRequireCompletion && !isHelixV2SearchComplete)
            ) {
                if (showLoading) {
                    setShowDataLoading(false)
                }
                return
            }

            try {
                const searchResults = await helixSearchV2Api.getResults({
                    sst: helixV2Summary.sst,
                    logId: helixV2Summary.logId,
                    pageNumber: filters.currentPageNumber,
                    perPage: filters.perPage,
                    filterOptions: {
                        networkSeedPersonIds: filters.networkLinksSelected,
                        minRelationshipScore:
                            convertMinRelationshipScoreToNumber(
                                filters.minRelationshipScore,
                            ),
                        mustBeInBooleanSearchResults:
                            filters.mustBeInBooleanSearchResults,
                        mustHaveRelationship: filters.mustHaveRelationship,
                        roleContextFilters: {
                            shortlistedValues:
                                convertShortlistedSelectedToV2FilterValue(
                                    filters.shortlistedSelected,
                                ),
                        },
                    },
                    sortOptions: convertOrderByToV2SortBy(filters.orderBy),
                })

                setTotalResultCount(searchResults.totalCount)

                if (searchResults.results.length > 0) {
                    const candidatePersonIds = searchResults.results.map(
                        (r) => r.personId,
                    )
                    const persons =
                        await personApi.getPersonsById(candidatePersonIds)

                    for (const person of persons.persons) {
                        if (!personCacheRef.current.has(person.id)) {
                            personCacheRef.current.set(person.id, person)
                        }
                    }

                    if (filters.networkLinksSelected.length > 0) {
                        const resolvedPersonLites = (
                            await networkApi.getNetworkScoped({
                                seedPersonIds: filters.networkLinksSelected,
                                resolvedPersonIds: candidatePersonIds,
                            })
                        ).resolvedPersonLites
                        setResolvedPersonLites(resolvedPersonLites)
                    }

                    const companyIds = new Set<string>()
                    for (const person of persons.persons) {
                        for (const workedAt of person.workedAts ?? []) {
                            companyIds.add(workedAt.companyId)
                        }
                    }

                    const companyCache = companyCacheRef.current
                    const missingCompanyIds = Array.from(companyIds).filter(
                        (id) => !companyCache.has(id),
                    )

                    if (missingCompanyIds.length > 0) {
                        const companies = await companyApi.getCompanies({
                            companyIds: missingCompanyIds,
                        })
                        for (const company of companies.companies) {
                            companyCache.set(company.id, company)
                        }
                    }
                }

                setSearchResults(searchResults.results)

                const presentMap = new Map<string, string>()

                if (currentRoleId) {
                    try {
                        const roleCandidates =
                            await fetchCandidatesForRole(currentRoleId)
                        for (const candidate of roleCandidates) {
                            presentMap.set(
                                candidate.candidatePersonId,
                                candidate.present as
                                    | PresentCandidateValues
                                    | 'all',
                            )
                        }
                        setStoreCandidates(roleCandidates)
                    } catch (e) {
                        console.error('Failed to fetch role candidates', e)
                        setStoreCandidates([])
                    } finally {
                        setRoleCandidatesLoaded(true)
                    }
                } else {
                    setStoreCandidates([])
                    setRoleCandidatesLoaded(false)
                }

                setHelixCandidateLiteDtos(
                    searchResults.results.map(
                        (result): HelixCandidateLiteDto => ({
                            candidatePersonId: result.personId,
                            present: currentRoleId
                                ? (presentMap.get(
                                      result.personId,
                                  ) as PresentCandidateValues)
                                : undefined,
                            rank: result.rank,
                            booleanSearchScoreNormalised:
                                result.booleanSearchScoreNormalised,
                            aggregatedRelationshipScore:
                                result.aggregatedRelationshipScore,
                            combinedScore: result.combinedScore,
                            llmRelevanceScoreScore:
                                result.llmRelevanceScoreScore,
                            llmRelevanceScoreReason:
                                result.llmRelevanceScoreReason,
                            llmRelevanceScoreTopPick:
                                result.llmRelevanceScoreTopPick,
                        }),
                    ),
                )

                const notAlreadyInRole = searchResults.results.filter(
                    (result) => !presentMap.has(result.personId),
                )

                setEphemeralCandidates(
                    notAlreadyInRole.map(
                        (result): CandidateLiteDto => ({
                            candidatePersonId: result.personId,
                            present: undefined,
                            contacted: false,
                            connections: [],
                            companyConnections: [],
                            rankingScore: 0,
                            closenessScore: 0,
                            jobRelevanceJobTitle: '',
                            jobRelevanceRawScore: 0,
                            jobRelevanceScore: 0,
                        }),
                    ),
                )

                setFiltersAreDirty(false)
                setShowResults(true)
            } catch (error) {
                console.error('Error loading search results:', error)
                setErrorMsg('Failed to load search results.')
            } finally {
                if (showLoading) {
                    setShowDataLoading(false)
                }
                // If the progress modal was opened because we found an sst on page load,
                // close it once results loading has finished (success or failure).
                if (pageLoadModalOpenRef.current) {
                    setShowHelixV2ProgressModal(false)
                    pageLoadModalOpenRef.current = false
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isHelixV2SearchComplete],
    )

    const cancelProgressModalAutoClose = useCallback(() => {
        if (progressModalAutoCloseTimeoutRef.current) {
            window.clearTimeout(progressModalAutoCloseTimeoutRef.current)
            progressModalAutoCloseTimeoutRef.current = null
        }
        setProgressModalAutoCloseDeadline(null)
    }, [])

    const startProgressModalAutoCloseCountdown = useCallback(() => {
        cancelProgressModalAutoClose()
        const deadline = Date.now() + HELIX_PROGRESS_MODAL_AUTO_CLOSE_DELAY_MS
        setProgressModalAutoCloseDeadline(deadline)
        progressModalAutoCloseTimeoutRef.current = window.setTimeout(() => {
            setProgressModalAutoCloseDeadline(null)
            progressModalAutoCloseTimeoutRef.current = null
            setShowHelixV2ProgressModal(false)
        }, HELIX_PROGRESS_MODAL_AUTO_CLOSE_DELAY_MS)
    }, [cancelProgressModalAutoClose, setShowHelixV2ProgressModal])

    const handleSearchFailure = useCallback(
        (message: string, options?: { suppressAlert?: boolean }) => {
            if (!options?.suppressAlert) {
                setErrorMsg(message)
            }
            cancelProgressModalAutoClose()
            setShowHelixV2ProgressModal(false)
        },
        [
            cancelProgressModalAutoClose,
            setShowHelixV2ProgressModal,
            setErrorMsg,
        ],
    )

    const handleFiltersChange = useCallback(
        (nextFilters: FilterInputs) => {
            const nextSeedSource = nextFilters.networkSeedPersonIds?.length
                ? nextFilters.networkSeedPersonIds
                : (nextFilters.trustedCirclePersonIds ?? [])
            const uniqueNextSeedIds = Array.from(new Set(nextSeedSource))

            setNetworkLinksIdsSelectedInNetworkLinks((prevIds) => {
                const idsAreEqual =
                    prevIds.length === uniqueNextSeedIds.length &&
                    prevIds.every(
                        (id, index) => id === uniqueNextSeedIds[index],
                    )

                if (idsAreEqual) {
                    return prevIds
                }

                return uniqueNextSeedIds
            })

            filtersRef.current = nextFilters
            setFilters(nextFilters)
        },
        [filtersRef],
    )

    type RunFiltersSearchOptions = {
        enableLlmScoring: boolean
        experienceOverride?: ExperienceBounds
        fromFiltersModal?: boolean
        onError?: (message: string) => void
    }

    const runFiltersSearch = useCallback(
        (nextFilters: FilterInputs, options: RunFiltersSearchOptions) => {
            const selectedSeedIds = Array.from(
                new Set(
                    nextFilters.networkSeedPersonIds?.length
                        ? nextFilters.networkSeedPersonIds
                        : (nextFilters.trustedCirclePersonIds ?? []),
                ),
            )
            setNetworkLinksIdsSelectedInNetworkLinks(selectedSeedIds)
            filtersRef.current = nextFilters
            setFilters(nextFilters)
            setBooleanQueryError(null)
            const composedQuery = buildBooleanQueryFromFilters(nextFilters)
            setQuery(composedQuery)
            setEnabledLLMScoring(options.enableLlmScoring)
            setResultsEnableLlmScoring(options.enableLlmScoring)
            if (options.experienceOverride) {
                const experienceCandidates = options.experienceOverride
                const minYears = clampExperienceYears(
                    Math.min(
                        experienceCandidates.minYears,
                        experienceCandidates.maxYears,
                    ),
                )
                const maxYears = clampExperienceYears(
                    Math.max(
                        experienceCandidates.minYears,
                        experienceCandidates.maxYears,
                    ),
                )
                const normalizedExperience = {
                    minYears,
                    maxYears,
                }
                experienceOverrideRef.current = normalizedExperience
                setStateIfChanged(
                    setMinExperienceYears,
                    normalizedExperience.minYears,
                )
                setStateIfChanged(
                    setMaxExperienceYears,
                    normalizedExperience.maxYears,
                )
            }
            void searchRef.current({
                overrideQuery: composedQuery,
                enableLlmScoring: options.enableLlmScoring,
                onError: options.onError,
                networkSeedPersonIds: selectedSeedIds,
            })
        },
        [clampExperienceYears],
    )

    const handleFiltersSearch = useCallback(
        (
            nextFilters: FilterInputs,
            experience?: ExperienceBounds,
            options?: SearchInvocationOptions,
        ) => {
            setFiltersModalSearchError(null)
            runFiltersSearch(nextFilters, {
                enableLlmScoring: false,
                experienceOverride: experience,
                fromFiltersModal: options?.source === 'filters_modal',
                onError:
                    options?.source === 'filters_modal'
                        ? (message) => {
                              setFiltersModalSearchError(message)
                              setFiltersModalOpenSignal((prev) => prev + 1)
                              setErrorMsg(null)
                          }
                        : undefined,
            })
        },
        [runFiltersSearch, setErrorMsg],
    )

    const handleSaveFilters = useCallback(
        async (
            nextFilters: FilterInputs,
            experience?: ExperienceBounds,
        ): Promise<void> => {
            const booleanSearchQuery =
                buildBooleanQueryFromFilters(nextFilters).trim()

            if (booleanSearchQuery.length === 0) {
                toast.error('Filters must include at least one value to save.')
                return
            }

            const targetMinYears = clampExperienceYears(
                experience?.minYears ?? minExperienceYears,
            )
            const targetMaxYears = clampExperienceYears(
                experience?.maxYears ?? maxExperienceYears,
            )

            const booleanSearchEvalParams: HelixBooleanSearchEvalParams = {
                booleanSearchQuery,
                autoIgnoreRetiredPeople: ignoreRetiredPeople,
            }

            if (targetMinYears > DEFAULT_MIN_EXPERIENCE_YEARS) {
                booleanSearchEvalParams.minExperienceMonths =
                    targetMinYears * 12
            }

            if (targetMaxYears < DEFAULT_MAX_EXPERIENCE_YEARS) {
                booleanSearchEvalParams.maxExperienceMonths =
                    targetMaxYears * 12
            }

            const seedIds = Array.from(
                new Set(
                    nextFilters.networkSeedPersonIds?.length
                        ? nextFilters.networkSeedPersonIds
                        : (nextFilters.trustedCirclePersonIds ?? []),
                ),
            )

            const request: StartHelixSearchV2Req = {
                roleId: roleId ?? undefined,
                booleanSearchEvalParams,
                relationshipEvalParams:
                    seedIds.length > 0
                        ? { networkSeedPersonIds: seedIds }
                        : undefined,
                enableLlmScoring: enabledLLMScoring,
                existingSst: helixV2SearchSummary?.sst ?? undefined,
                searchFilters: buildHelixSearchFiltersPayload(nextFilters),
            }

            setIsSavingFilters(true)
            try {
                await helixSearchV2Api.persistFilters(request)
                setFiltersAreDirty(false)
                toast.success('Filters saved.')
            } catch (error) {
                console.error('Failed to save filters', error)
                toast.error(
                    'Unable to save filters right now. Please try again.',
                )
            } finally {
                setIsSavingFilters(false)
            }
        },
        [
            clampExperienceYears,
            minExperienceYears,
            maxExperienceYears,
            ignoreRetiredPeople,
            roleId,
            enabledLLMScoring,
            helixV2SearchSummary?.sst,
        ],
    )

    const handleFiltersSearchWithAi = useCallback(
        (
            nextFilters: FilterInputs,
            jobDescription?: string,
            experience?: ExperienceBounds,
            options?: SearchInvocationOptions,
        ) => {
            void jobDescription
            setFiltersModalSearchError(null)
            runFiltersSearch(nextFilters, {
                enableLlmScoring: true,
                experienceOverride: experience,
                fromFiltersModal: options?.source === 'filters_modal',
                onError:
                    options?.source === 'filters_modal'
                        ? (message) => {
                              setFiltersModalSearchError(message)
                              setFiltersModalOpenSignal((prev) => prev + 1)
                              setErrorMsg(null)
                          }
                        : undefined,
            })
        },
        [runFiltersSearch],
    )

    const pollForSearchCompletion = useCallback(
        async (
            sst: string,
            filters: FilterData,
            v2SummaryOverride?: HelixSearchV2Summary,
            invocationOptions?: {
                onError?: (message: string) => void
            },
        ) => {
            setShowResults(false)
            setErrorMsg(null)

            helixSearchPollAbortRef.current?.()

            const handlePollingFailure = (message: string) => {
                handleSearchFailure(message, {
                    suppressAlert: Boolean(invocationOptions?.onError),
                })
                invocationOptions?.onError?.(message)
            }

            let initialStatus: GetHelixSearchStatusRes
            try {
                initialStatus = await helixSearchV2Api.getSearchStatus({
                    sst,
                })
            } catch (error) {
                console.error('Failed to fetch Helix V2 status', error)
                handlePollingFailure(
                    'There was an error performing the search. Please try again.',
                )
                return
            }
            applyHelixStatusUpdate(initialStatus, {
                resetFilters: true,
                targetFilters: filters,
            })

            if (initialStatus.sessionStatus === 'EXPIRED') {
                autoRestartSstRef.current = initialStatus.sst
                setShowHelixV2ProgressModal(false)
                return
            }

            let interval: number | null = null
            let isCancelled = false

            const cleanup = () => {
                if (interval !== null) {
                    window.clearInterval(interval)
                    interval = null
                }
                isCancelled = true
                if (helixSearchPollAbortRef.current === cleanup) {
                    helixSearchPollAbortRef.current = null
                }
            }

            helixSearchPollAbortRef.current = cleanup

            const poll = async () => {
                if (isCancelled) {
                    return
                }
                try {
                    const status = await helixSearchV2Api.getSearchStatus({
                        sst,
                    })
                    if (isCancelled) {
                        return
                    }
                    applyHelixStatusUpdate(status, { targetFilters: filters })

                    switch (status.sessionStatus) {
                        case 'COMPLETE':
                            cleanup()
                            await loadResults(
                                v2SummaryOverride ?? {
                                    sst: status.sst,
                                    logId: status.logId,
                                },
                                filters,
                                false,
                                true,
                            )
                            startProgressModalAutoCloseCountdown()
                            break
                        case 'FAILED':
                        case 'CANCELED':
                        case 'CANCELLING':
                            cleanup()
                            handlePollingFailure(
                                'There was an error performing the search. Please try again.',
                            )
                            setShowHelixV2ProgressModal(false)
                            cancelProgressModalAutoClose()
                            break
                        case 'EXPIRED':
                            cleanup()
                            autoRestartSstRef.current = status.sst
                            setShowHelixV2ProgressModal(false)
                            cancelProgressModalAutoClose()
                            break
                        default:
                            break
                    }
                } catch (err) {
                    if (isCancelled) {
                        return
                    }
                    cleanup()
                    console.error(err)
                    handlePollingFailure(
                        'There was an error performing the search. Please try again.',
                    )
                }
            }

            interval = window.setInterval(() => {
                void poll()
            }, 3000)
            await poll()
        },
        [
            applyHelixStatusUpdate,
            loadResults,
            setErrorMsg,
            startProgressModalAutoCloseCountdown,
            handleSearchFailure,
            cancelProgressModalAutoClose,
        ],
    )

    const startHelixV2Search = useCallback(
        async (
            baseQuery: string,
            options?: {
                enableLlmScoring?: boolean
                overrideNetworkSeedPersonIds?: string[]
            },
        ) => {
            const trimmedQuery = baseQuery.trim()

            if (trimmedQuery === '') {
                throw new Error('Boolean query cannot be empty')
            }

            const llmScoring = options?.enableLlmScoring ?? enabledLLMScoring

            const experienceOverride = experienceOverrideRef.current
            const effectiveMinYears =
                experienceOverride?.minYears ?? minExperienceYears
            const effectiveMaxYears =
                experienceOverride?.maxYears ?? maxExperienceYears
            experienceOverrideRef.current = null

            const booleanEvalParams: {
                booleanSearchQuery: string
                autoIgnoreRetiredPeople: boolean
                minExperienceMonths?: number
                maxExperienceMonths?: number
            } = {
                booleanSearchQuery: trimmedQuery,
                autoIgnoreRetiredPeople: ignoreRetiredPeople,
            }

            const minExperienceMonths =
                effectiveMinYears > DEFAULT_MIN_EXPERIENCE_YEARS
                    ? effectiveMinYears * 12
                    : undefined
            const maxExperienceMonths =
                effectiveMaxYears < DEFAULT_MAX_EXPERIENCE_YEARS
                    ? effectiveMaxYears * 12
                    : undefined

            if (minExperienceMonths !== undefined) {
                booleanEvalParams.minExperienceMonths = minExperienceMonths
            }
            if (maxExperienceMonths !== undefined) {
                booleanEvalParams.maxExperienceMonths = maxExperienceMonths
            }

            const relationshipSeedIds =
                options?.overrideNetworkSeedPersonIds ??
                networkLinksIdsSelectedInNetworkLinks

            const relationshipEvalParams =
                relationshipSeedIds.length > 0
                    ? {
                          networkSeedPersonIds: relationshipSeedIds,
                      }
                    : undefined

            try {
                setResultsEnableLlmScoring(llmScoring)
                const searchFiltersPayload = buildHelixSearchFiltersPayload(
                    filtersRef.current,
                )
                const response = await helixSearchV2Api.startNewHelixSearch({
                    roleId: roleId ?? undefined,
                    booleanSearchEvalParams: booleanEvalParams,
                    relationshipEvalParams,
                    enableLlmScoring: llmScoring,
                    existingSst: helixV2SearchSummary?.sst ?? undefined,
                    searchFilters: searchFiltersPayload,
                })
                hydratedFiltersSstRef.current = response.searchSessionToken

                setHelixV2SearchSummary({
                    sst: response.searchSessionToken,
                    logId: response.logId,
                })
                setIsHelixV2SearchComplete(false)
                urlParams.set('helixV2Sst', response.searchSessionToken)
                navigate(`?${urlParams.toString()}`, { replace: true })
                cancelProgressModalAutoClose()
                setShowHelixV2ProgressModal(true)
                return response
            } catch (error) {
                console.error('Failed to start Helix V2 search', error)
                toast.error('Failed to start Helix V2 search.')
                throw error
            }
        },
        [
            ignoreRetiredPeople,
            minExperienceYears,
            maxExperienceYears,
            networkLinksIdsSelectedInNetworkLinks,
            enabledLLMScoring,
            roleId,
            helixV2SearchSummary?.sst,
            urlParams,
            navigate,
            cancelProgressModalAutoClose,
            filtersRef,
        ],
    )

    const resolveNetworkImportPromise = useCallback(() => {
        networkImportPromiseResolveRef.current?.()
        networkImportPromiseResolveRef.current = null
        networkImportPromiseRejectRef.current = null
        networkImportPromiseRef.current = null
    }, [])

    const rejectNetworkImportPromise = useCallback((error?: Error) => {
        networkImportPromiseRejectRef.current?.(error)
        networkImportPromiseResolveRef.current = null
        networkImportPromiseRejectRef.current = null
        networkImportPromiseRef.current = null
    }, [])

    const ensureNetworkImportReady = useCallback(
        async (seedIds?: string[]) => {
            const effectiveSeedIds =
                seedIds ?? networkLinksIdsSelectedInNetworkLinks

            if (effectiveSeedIds.length === 0) {
                return
            }

            if (networkImportPromiseRef.current) {
                return networkImportPromiseRef.current
            }

            let idsNeedingImport: string[] = []
            try {
                const statuses =
                    await personApi.getNetworkImportStatuses(effectiveSeedIds)
                idsNeedingImport = effectiveSeedIds.filter(
                    (id) => statuses[id]?.status !== 'COMPLETE',
                )
            } catch (error) {
                console.error('Failed to check network import statuses', error)
                throw new Error(
                    'Unable to verify your Trusted Circle imports. Please retry.',
                )
            }

            if (idsNeedingImport.length === 0) {
                return
            }

            const promise = new Promise<void>((resolve, reject) => {
                networkImportPromiseResolveRef.current = resolve
                networkImportPromiseRejectRef.current = reject
            })
            networkImportPromiseRef.current = promise
            setNetworkImportModalState({
                personIds: idsNeedingImport,
                personNamesById: networkPersonNamesById,
                personPhotoUrlsById: networkPersonPhotoUrlsById,
            })
            return promise
        },
        [
            networkLinksIdsSelectedInNetworkLinks,
            networkPersonNamesById,
            networkPersonPhotoUrlsById,
        ],
    )

    const handleNetworkImportComplete = useCallback(() => {
        setNetworkImportModalState(null)
        resolveNetworkImportPromise()
    }, [resolveNetworkImportPromise])

    const handleNetworkImportCancel = useCallback(
        (err?: Error) => {
            setNetworkImportModalState(null)
            rejectNetworkImportPromise(
                err ?? new Error('Network import was canceled.'),
            )
        },
        [rejectNetworkImportPromise],
    )

    useEffect(() => {
        return () => {
            if (networkImportPromiseRef.current) {
                rejectNetworkImportPromise(
                    new Error('Network import was interrupted.'),
                )
            }
        }
    }, [rejectNetworkImportPromise])

    useEffect(() => {
        return () => {
            helixSearchPollAbortRef.current?.()
            helixSearchPollAbortRef.current = null
        }
    }, [])

    useEffect(() => {
        return () => {
            cancelProgressModalAutoClose()
        }
    }, [cancelProgressModalAutoClose])

    useEffect(() => {
        if (
            helixV2StatusDetails &&
            helixV2StatusDetails.sessionStatus === 'EXPIRED' &&
            autoRestartSstRef.current === helixV2StatusDetails.sst
        ) {
            autoRestartSstRef.current = null
            void searchRef.current()
        }
    }, [helixV2StatusDetails])

    const handleHelixProgressModalClose = useCallback(() => {
        cancelProgressModalAutoClose()
        setShowHelixV2ProgressModal(false)
    }, [cancelProgressModalAutoClose])

    useEffect(() => {
        if (!showResults || showHelixV2ProgressModal) {
            return
        }
        networkViewRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }, [showResults, showHelixV2ProgressModal])

    // const handleHelixSearchCancel = useCallback(() => {
    //     helixSearchPollAbortRef.current?.()
    //     helixSearchPollAbortRef.current = null
    //     cancelProgressModalAutoClose()
    //     setShowHelixV2ProgressModal(false)
    //     setIsHelixV2SearchComplete(false)
    //     setHelixV2SearchSummary(null)
    //     setHelixV2StatusDetails(null)
    //     setShowResults(false)
    //     setSearchResults([])
    //     setHelixCandidateLiteDtos([])
    //     setShowDataLoading(false)
    //     setTotalResultCount(0)
    //     setErrorMsg(null)
    //     if (urlParams.has('helixV2Sst')) {
    //         urlParams.delete('helixV2Sst')
    //         const nextSearch = urlParams.toString()
    //         navigate(nextSearch ? `?${nextSearch}` : '?', { replace: true })
    //     }
    //     toast.info('Helix search canceled.')
    // }, [cancelProgressModalAutoClose, navigate, urlParams])

    const pageLoadRan = useRef<boolean>(false)
    useEffect(() => {
        if (pageLoadRan.current === true) {
            return
        }

        pageLoadRan.current = true
        const urlParams = new URLSearchParams(location.search)

        const roleIdFromUrl = urlParams.get('roleId')
        if (roleIdFromUrl) {
            setRoleId(roleIdFromUrl)
            ;(async () => {
                let roleLiteRes: GetRoleLiteRes | null = null
                try {
                    roleLiteRes = await roleApi.getLite({
                        roleId: roleIdFromUrl,
                        includeNetworkLinkIds: true,
                        includeHiringTeamIds: true,
                    })
                    setRoleLite(roleLiteRes.role)
                    setRoleLiteInStore(roleLiteRes.role)

                    const networkLinkIds = [
                        ...(roleLiteRes.role.networkLinkIds ?? []),
                        ...(roleLiteRes.role.hiringTeamIds ?? []),
                    ]
                    const uniqueNetworkLinkIds = Array.from(
                        new Set(networkLinkIds),
                    )

                    if (uniqueNetworkLinkIds.length > 0) {
                        const response = await personApi.getPersonsLite({
                            personIds: uniqueNetworkLinkIds,
                            includeCurrentJobDetails: true,
                        })

                        for (const person of response.persons) {
                            personLiteCacheRef.current.set(person.id, person)
                        }

                        setNetworkLinksIdsSelectedInNetworkLinks(
                            uniqueNetworkLinkIds,
                        )
                        setNetworkLinksSelectedInNetworkLinks(response.persons)
                    }
                } catch (error) {
                    console.error(
                        'Failed to load role or network link people:',
                        error,
                    )
                }
                setInitialRoleLoadComplete(true)
            })()
        } else {
            setInitialRoleLoadComplete(true)
        }

        const helixV2SstFromUrl =
            urlParams.get('helixV2Sst') ?? urlParams.get('sst')

        if (helixV2SstFromUrl) {
            void pollForSearchCompletion(
                helixV2SstFromUrl,
                {
                    currentPageNumber,
                    perPage,
                    networkLinksSelected: networkLinksSelectedInFilters,
                    minRelationshipScore,
                    mustBeInBooleanSearchResults,
                    mustHaveRelationship,
                    shortlistedSelected,
                    orderBy,
                },
                helixV2SearchSummary ?? undefined,
            )
        } else {
            // Optionally do something if no sst exists
        }
    }, [
        currentPageNumber,
        helixV2SearchSummary,
        minRelationshipScore,
        mustBeInBooleanSearchResults,
        mustHaveRelationship,
        networkLinksSelectedInFilters,
        orderBy,
        perPage,
        pollForSearchCompletion,
        setRoleLiteInStore,
        shortlistedSelected,
        location.search,
    ])

    const resetResults = useCallback(() => {
        setShowResults(false)
        setSearchResults([])
        setHelixCandidateLiteDtos([])
        setSelectedCandidateId(null)
        setRightPaneMode('search_filters')
    }, [])

    const clearRoleContext = useCallback(() => {
        resetFilters([], false)
        resetResults()

        filtersRef.current = createDefaultFilterInputs([])
        setFilters(filtersRef.current)

        setRoleId(null)
        setRoleLite(null)
        setInitialSavedCandidateCount(null)
        setRoleCandidatesLoaded(false)
        setNetworkLinksIdsSelectedInNetworkLinks([])
        setNetworkLinksSelectedInNetworkLinks([])
        setNetworkLinksSelectedInFilters([])
        setNetworkLinkIdsSetInThisSearch([])
        setMinExperienceYears(DEFAULT_MIN_EXPERIENCE_YEARS)
        setMaxExperienceYears(DEFAULT_MAX_EXPERIENCE_YEARS)
        setEnabledLLMScoring(false)
        setResultsEnableLlmScoring(false)
        setQuery('developer OR engineer')
        setBooleanQueryError(null)
        setTrustedCircleError(null)
        setShowDataLoading(false)
        setFilteredCandidatePoolCount(0)
        setFilteredTopPickCount(0)
        setHelixV2SearchSummary(null)
        setIsHelixV2SearchComplete(false)
        setHelixV2StatusDetails(null)
        setErrorMsg(null)
        cancelProgressModalAutoClose()
        setShowHelixV2ProgressModal(false)
        hydratedFiltersSstRef.current = null
        autoRestartSstRef.current = null
        setFiltersModalOpenSignal((prev) => prev + 1)
    }, [cancelProgressModalAutoClose, resetFilters, resetResults])

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search)
        const incomingRoleId = urlParams.get('roleId')

        if (incomingRoleId || !roleId) {
            return
        }

        clearRoleContext()
    }, [location.search, roleId, clearRoleContext])

    useEffect(() => {
        const nextDescription = roleLite?.jobDescription ?? null
        if (roleDescriptionForFiltersRef.current !== nextDescription) {
            roleDescriptionForFiltersRef.current = nextDescription
            setRoleFiltersRefreshSignal((prev) => prev + 1)
        }
    }, [roleLite?.jobDescription])

    useEffect(() => {
        if (
            hasOpenedTrustedCircleModalRef.current ||
            !initialRoleLoadComplete
        ) {
            return
        }

        const roleTrustedCircleCount =
            (roleLite?.networkLinkIds?.length ?? 0) +
            (roleLite?.hiringTeamIds?.length ?? 0)

        const shouldOpenTrustedCircleModal =
            networkLinksIdsSelectedInNetworkLinks.length === 0 &&
            (!roleId || roleTrustedCircleCount === 0)

        if (shouldOpenTrustedCircleModal) {
            setIsModalOpen(true)
        }

        hasOpenedTrustedCircleModalRef.current = true
    }, [
        initialRoleLoadComplete,
        networkLinksIdsSelectedInNetworkLinks,
        roleId,
        roleLite,
    ])

    async function search(options?: {
        overrideQuery?: string
        enableLlmScoring?: boolean
        onError?: (message: string) => void
        networkSeedPersonIds?: string[]
    }) {
        resetResults()
        setErrorMsg(null)

        const effectiveNetworkSeedIds =
            options?.networkSeedPersonIds ??
            networkLinksIdsSelectedInNetworkLinks

        if (effectiveNetworkSeedIds.length === 0) {
            const message = 'Please add people to your Trusted Circle.'
            setTrustedCircleError(message)
            options?.onError?.(message)
            return
        }

        setTrustedCircleError(null)
        const baseQuery = options?.overrideQuery ?? query
        const trimmedQuery = baseQuery.trim()
        if (trimmedQuery === '') {
            const message = 'Please introduce a boolean query.'
            setBooleanQueryError(message)
            options?.onError?.(message)
            return
        }

        setBooleanQueryError(null)
        let queryString = trimmedQuery

        try {
            // TODO : ignoreRetiredPeople is always true now - refactor
            if (ignoreRetiredPeople) {
                queryString = `(${queryString}) AND NOT (headline:"retired" OR summary:"retired" OR jobTitle:"retired" OR company:"retired" OR jobDescription:"retired")`
            }

            parse(queryString) // validate query syntax
        } catch (error: unknown) {
            const message =
                error instanceof Error
                    ? `Boolean query syntax is invalid. ${error.message}`
                    : 'Boolean query syntax is invalid.'
            if (!options?.onError) {
                setErrorMsg(message)
            }
            options?.onError?.(message)
            return
        }

        const llmScoringForSearch =
            options?.enableLlmScoring ?? enabledLLMScoring

        try {
            await ensureNetworkImportReady(effectiveNetworkSeedIds)
        } catch (error) {
            console.error('Network import step failed:', error)
            const message =
                error instanceof Error && error.message
                    ? error.message
                    : 'Unable to import your network. Please try again.'
            toast.error(message)
            options?.onError?.(message)
            return
        }

        let helixV2Start: StartHelixSearchV2Res | null = null
        try {
            helixV2Start = await startHelixV2Search(trimmedQuery, {
                enableLlmScoring: llmScoringForSearch,
                overrideNetworkSeedPersonIds: effectiveNetworkSeedIds,
            })
        } catch (error) {
            const message = `Failed to start Helix V2 search: ${error}`
            if (!options?.onError) {
                setErrorMsg(message)
            }
            options?.onError?.(message)
            return
        }

        try {
            const resetFilterData = resetFilters(
                effectiveNetworkSeedIds,
                llmScoringForSearch,
            )
            const helixV2SstForPolling =
                helixV2Start?.searchSessionToken ??
                helixV2SearchSummary?.sst ??
                urlParams.get('helixV2Sst') ??
                undefined

            if (!helixV2SstForPolling) {
                throw new Error('Helix V2 search session token not found.')
            }

            await pollForSearchCompletion(
                helixV2SstForPolling,
                resetFilterData,
                helixV2Start
                    ? {
                          sst: helixV2Start.searchSessionToken,
                          logId: helixV2Start.logId,
                      }
                    : (helixV2SearchSummary ?? undefined),
                options,
            )
        } catch (error) {
            console.error('Search failed:', error)
            handleSearchFailure('Search failed.', {
                suppressAlert: Boolean(options?.onError),
            })
            options?.onError?.('Search failed.')
        }
    }

    searchRef.current = search

    // const handleSearchClick = useCallback(() => {
    //     void searchRef.current()
    // }, [])

    // const handleHelixSearchRetry = useCallback(() => {
    //     cancelProgressModalAutoClose()
    //     void searchRef.current()
    // }, [cancelProgressModalAutoClose])

    const handleAddPeopleClick = useCallback(() => {
        setIsModalOpen(true)
    }, [])

    // const handleShowHelp = useCallback(() => {
    //     setRightPaneMode('boolean_search_help')
    // }, [])

    const handleOpenSearchFiltersPane = useCallback(() => {
        setRightPaneMode('search_filters')
    }, [setRightPaneMode])

    const handleDataLoadingClose = useCallback(() => {
        setShowDataLoading(false)
    }, [])

    // ############## NEW CONTROL FLOW - end ##############

    // fired when the selected network links change
    useEffect(() => {
        async function fetchNetworkLinks() {
            if (networkLinksIdsSelectedInNetworkLinks.length === 0) {
                setNetworkLinksSelectedInNetworkLinks([])
                return
            }

            // Determine which personIds are missing from the cache
            const cache = personLiteCacheRef.current
            const missingIds = networkLinksIdsSelectedInNetworkLinks.filter(
                (id) => !cache.has(id),
            )

            // No need to fetch if everything is already cached
            if (missingIds.length === 0) {
                const cachedPersons = networkLinksIdsSelectedInNetworkLinks.map(
                    (id) => cache.getOrThrow(id),
                )
                setNetworkLinksSelectedInNetworkLinks(cachedPersons)
                return
            }

            try {
                const response = await personApi.getPersonsLite({
                    personIds: missingIds,
                    includeCurrentJobDetails: true,
                })

                // Update the cache with new data
                for (const person of response.persons) {
                    cache.set(person.id, person)
                }

                // Always build the list from the full input (cached + new)
                const fullList = networkLinksIdsSelectedInNetworkLinks
                    .map((id) => cache.get(id))
                    .filter(Boolean) as PersonLiteDto[]

                setNetworkLinksSelectedInNetworkLinks(fullList)
            } catch (error) {
                console.error('Failed to fetch missing network links:', error)
            }
        }

        fetchNetworkLinks()
    }, [networkLinksIdsSelectedInNetworkLinks])

    useEffect(() => {
        if (
            trustedCircleError &&
            networkLinksIdsSelectedInNetworkLinks.length > 0
        ) {
            setTrustedCircleError(null)
        }
    }, [trustedCircleError, networkLinksIdsSelectedInNetworkLinks])

    async function addNetworkLinksIds(newIds: string[]) {
        const uniqueNewIds = Array.from(new Set(newIds))
        if (uniqueNewIds.length === 0) {
            return
        }

        const updatedIds = [
            ...new Set([
                ...networkLinksIdsSelectedInNetworkLinks,
                ...uniqueNewIds,
            ]),
        ]

        if (roleId) {
            await roleApi.addNetworkLinks({
                roleId,
                personIds: uniqueNewIds,
            })
        }

        setNetworkLinksIdsSelectedInNetworkLinks(updatedIds)

        const cache = personLiteCacheRef.current
        const missingIds = uniqueNewIds.filter((id) => !cache.has(id))

        if (missingIds.length > 0) {
            const response = await personApi.getPersonsLite({
                personIds: missingIds,
                includeCurrentJobDetails: true,
            })
            for (const person of response.persons) {
                cache.set(person.id, person)
            }
        }

        const fullList = updatedIds
            .map((id) => cache.get(id))
            .filter(Boolean) as PersonLiteDto[]

        setNetworkLinksSelectedInNetworkLinks(fullList)
    }

    async function addNetworkLinksFromDepartments(newIds: string[]) {
        await addNetworkLinksIds(newIds)
    }

    async function addNetworkLinksModalClosed(
        newIds: string[],
        confirmed: boolean,
    ) {
        if (!confirmed || newIds.length === 0) {
            setIsModalOpen(false)
            return
        }

        await addNetworkLinksIds(newIds)
        setIsModalOpen(false)
    }

    // const handleRemoveNetworkLink = useCallback(async (idToRemove: string) => {
    //     try {
    //         // we no longer remove network links from the role when they are removed from the trusted circle
    //         // if (roleId) {
    //         //     await roleApi.deleteNetworkLinks({
    //         //         personIds: [idToRemove],
    //         //         roleId: roleId,
    //         //     })
    //         // }
    //         setNetworkLinksIdsSelectedInNetworkLinks((prev) =>
    //             prev.filter((id) => id !== idToRemove),
    //         )
    //         setNetworkLinksSelectedInNetworkLinks((prev) =>
    //             prev.filter((person) => person.id !== idToRemove),
    //         )
    //     } catch (error) {
    //         console.error('Failed to remove role from network links:', error)
    //     }
    // }, [])

    const handleLiveNetworkLinkSelection = useCallback(
        (person: PersonLiteDto, selected: boolean) => {
            const id = person.id

            if (selected) {
                setNetworkLinksIdsSelectedInNetworkLinks((prev) => [
                    ...new Set([...prev, id]),
                ])
                setNetworkLinksSelectedInNetworkLinks((prev) => {
                    if (prev.some((p) => p.id === id)) return prev
                    return [...prev, person]
                })
            } else {
                setNetworkLinksIdsSelectedInNetworkLinks((prev) =>
                    prev.filter((existingId) => existingId !== id),
                )
                setNetworkLinksSelectedInNetworkLinks((prev) =>
                    prev.filter((p) => p.id !== id),
                )
            }
        },
        [],
    )

    async function fetchCandidatesForRole(roleId: string) {
        const candidateCount = await roleApi.getCandidateCountForRole({
            roleId,
            shortlisted: 'all',
        })

        if (candidateCount === 0) {
            return []
        }

        const { candidates } = await roleApi.getCandidates({
            roleId,
            pageSize: candidateCount,
            pageNumber: 1,
            orderBy: 'combined',
            presentFilter: 'all',
            interestedFilter: 'all',
        })

        return candidates
    }

    // ##### filter functions - start #####
    const latestRequestIdRef = useRef(0)

    const getFilteredCandidatePoolCount = useCallback(async () => {
        const requestId = ++latestRequestIdRef.current

        if (!helixV2SearchSummary || !isHelixV2SearchComplete) {
            if (requestId === latestRequestIdRef.current) {
                setFilteredCandidatePoolCountIsLoading(false)
            }
            return
        }

        setFilteredCandidatePoolCountIsLoading(true)

        const filters = filterRef.current
        const llmScoringEnabledForRun =
            helixV2StatusDetails?.enableLlmScoring ?? resultsEnableLlmScoring

        try {
            const orderByData = convertOrderByToV2SortBy(filters.orderBy)
            const filterOptions = {
                networkSeedPersonIds: filters.networkLinksSelected,
                mustBeInBooleanSearchResults:
                    filters.mustBeInBooleanSearchResults,
                mustHaveRelationship: filters.mustHaveRelationship,
                minRelationshipScore: convertMinRelationshipScoreToNumber(
                    filters.minRelationshipScore,
                ),
                roleContextFilters: {
                    shortlistedValues:
                        convertShortlistedSelectedToV2FilterValue(
                            filters.shortlistedSelected,
                        ),
                },
            }

            const [candidatePoolCountRes, topPickCountRes] = await Promise.all([
                helixSearchV2Api.getResultsCount({
                    sst: helixV2SearchSummary.sst,
                    logId: helixV2SearchSummary.logId,
                    filterOptions,
                    sortOptions: orderByData,
                }),
                llmScoringEnabledForRun
                    ? helixSearchV2Api.getResultsCount({
                          sst: helixV2SearchSummary.sst,
                          logId: helixV2SearchSummary.logId,
                          filterOptions: {
                              ...filterOptions,
                              mustBeTopPick: true,
                          },
                          sortOptions: orderByData,
                      })
                    : Promise.resolve(null),
            ])

            if (requestId === latestRequestIdRef.current) {
                setFilteredCandidatePoolCount(candidatePoolCountRes.totalCount)
                setFilteredTopPickCount(topPickCountRes?.totalCount ?? 0)
            }
        } catch (err) {
            if (requestId === latestRequestIdRef.current) {
                console.error(
                    'Failed to get filtered candidate pool count:',
                    err,
                )
            }
        } finally {
            if (requestId === latestRequestIdRef.current) {
                setFilteredCandidatePoolCountIsLoading(false)
            }
        }
    }, [
        helixV2SearchSummary,
        helixV2StatusDetails?.enableLlmScoring,
        isHelixV2SearchComplete,
        resultsEnableLlmScoring,
    ])

    // automatically update the candidate pool count when any of the filter values change
    useEffect(() => {
        getFilteredCandidatePoolCount()
    }, [
        shortlistedSelected,
        networkLinksSelectedInFilters,
        minRelationshipScore,
        mustBeInBooleanSearchResults,
        mustHaveRelationship,
        getFilteredCandidatePoolCount,
    ])

    const handleFilterApplyClick = useCallback(async () => {
        setCurrentPageNumber(1)
        if (!helixV2SearchSummary) {
            toast.error('Start a Helix search to view results.')
            return
        }
        if (!isHelixV2SearchComplete) {
            toast.error(
                'Search is still running. Please wait for it to finish before applying filters.',
            )
            return
        }

        await loadResults(
            helixV2SearchSummary,
            {
                currentPageNumber: 1,
                perPage,
                networkLinksSelected: networkLinksSelectedInFilters,
                minRelationshipScore,
                mustBeInBooleanSearchResults,
                mustHaveRelationship,
                shortlistedSelected,
                orderBy,
            },
            true,
        )
    }, [
        helixV2SearchSummary,
        isHelixV2SearchComplete,
        loadResults,
        minRelationshipScore,
        mustBeInBooleanSearchResults,
        mustHaveRelationship,
        networkLinksSelectedInFilters,
        orderBy,
        perPage,
        shortlistedSelected,
    ])

    const handleFilterResetClick = useCallback(async () => {
        const resetFilterData = resetFilters(
            networkLinkIdsSetInThisSearch,
            resultsEnableLlmScoring,
        )
        if (helixV2SearchSummary && isHelixV2SearchComplete) {
            await loadResults(helixV2SearchSummary, resetFilterData, true)
        }
    }, [
        helixV2SearchSummary,
        isHelixV2SearchComplete,
        loadResults,
        networkLinkIdsSetInThisSearch,
        resultsEnableLlmScoring,
        resetFilters,
    ])

    const networkLinksSelectedChanged = useCallback(
        (selectedIds: string[]) => {
            setNetworkLinksSelectedInFilters(selectedIds)
            setFiltersAreDirty(true)
        },
        [setFiltersAreDirty, setNetworkLinksSelectedInFilters],
    )
    const shortlistedSelectedChanged = useCallback(
        (selectedIds: string[]) => {
            setShortlistedSelected(selectedIds)
            setFiltersAreDirty(true)
        },
        [setFiltersAreDirty, setShortlistedSelected],
    )
    const minRelationshipScoreChanged = useCallback(
        (value: string) => {
            setMinRelationshipScore(value)
            setFiltersAreDirty(true)
        },
        [setFiltersAreDirty, setMinRelationshipScore],
    )
    const orderByChanged = useCallback(
        (value: string) => {
            setOrderBy(value)
            setFiltersAreDirty(true)
        },
        [setFiltersAreDirty, setOrderBy],
    )
    const mustBeInBooleanSearchResultsChanged = useCallback(
        (checked: boolean) => {
            setMustBeInBooleanSearchResults(checked)
            setFiltersAreDirty(true)
        },
        [setFiltersAreDirty, setMustBeInBooleanSearchResults],
    )
    const mustHaveRelationshipChanged = useCallback(
        (checked: boolean) => {
            setMustHaveRelationship(checked)
            setFiltersAreDirty(true)
        },
        [setFiltersAreDirty, setMustHaveRelationship],
    )
    // ##### filter functions - end #####

    const pageChanged = useCallback(
        async (pageNumber: number) => {
            // console.log(
            //     `Page changed to: ${pageNumber} Per page: ${filterRef.current.perPage}`,
            // )
            setCurrentPageNumber(pageNumber)

            if (helixV2SearchSummary && isHelixV2SearchComplete) {
                const filters = {
                    ...filterRef.current,
                    currentPageNumber: pageNumber,
                }

                await loadResults(helixV2SearchSummary, filters, true)
            }
        },
        [helixV2SearchSummary, isHelixV2SearchComplete, loadResults],
    )

    const perPageChanged = useCallback(
        async (newPerPage: number) => {
            // console.log(
            //     `Per page changed to: ${newPerPage} Page number: ${currentPageNumber} BUT we'll use 1`,
            // )
            setPerPage(newPerPage)
            setCurrentPageNumber(1)

            if (helixV2SearchSummary && isHelixV2SearchComplete) {
                const filters = {
                    ...filterRef.current,
                    currentPageNumber: 1,
                    perPage: newPerPage,
                }

                await loadResults(helixV2SearchSummary, filters, true)
            }
        },
        [helixV2SearchSummary, isHelixV2SearchComplete, loadResults],
    )

    const handleCandidateSelection = useCallback(
        async (candidateId: string) => {
            setSelectedCandidateId(candidateId)
            setRightPaneMode('candidate')
        },
        [],
    )

    useEffect(() => {
        selectedCandidateIdRef.current = selectedCandidateId
    }, [selectedCandidateId])

    useEffect(() => {
        if (helixCandidateLiteDtos.length === 0) {
            if (selectedCandidateIdRef.current !== null) {
                setSelectedCandidateId(null)
                setRightPaneMode('search_filters')
            }
            return
        }

        const firstCandidateId =
            helixCandidateLiteDtos[0].candidatePersonId ?? null

        if (
            firstCandidateId &&
            selectedCandidateIdRef.current !== firstCandidateId
        ) {
            setSelectedCandidateId(firstCandidateId)
            setRightPaneMode('candidate')
        }
    }, [helixCandidateLiteDtos])

    const getCandidateRank = useCallback(
        (candidate: GenericCandidateLite) => candidate.rank ?? 0,
        [],
    )

    const selectedCandidateConnections = useMemo(() => {
        if (!selectedCandidateId) {
            return EMPTY_CONNECTION_DETAILS
        }

        const resolved = resolvedPersonLites.find(
            (p) => p.personId === selectedCandidateId,
        )

        return resolved?.connectionDetails ?? EMPTY_CONNECTION_DETAILS
    }, [resolvedPersonLites, selectedCandidateId])

    // const handleBooleanQueryChange = useCallback(
    //     (newQuery: string) => {
    //         setQuery(newQuery)
    //         if (booleanQueryError && newQuery.trim() !== '') {
    //             setBooleanQueryError(null)
    //         }
    //     },
    //     [booleanQueryError],
    // )

    const handleSaveAllCandidatesToRole = useCallback(async () => {
        if (!roleId) return

        const { candidates: storeCandidates, ephemeralCandidates } =
            manageCandidateStore.getState()

        const candidatePresentById = buildCandidatePresentMap(
            storeCandidates,
            ephemeralCandidates,
        )

        const candidatesToSave = getCandidatesNeedingSave(
            helixCandidateLiteDtos,
            candidatePresentById,
        )

        if (candidatesToSave.length === 0) {
            toast.success('All candidates are already saved to the role.')
            return
        }

        const candidateIds = candidatesToSave.map(
            (candidate) => candidate.candidatePersonId,
        )

        const savedCandidateIds = new Set(candidateIds)
        const updatedStoreCandidates = storeCandidates.map((candidate) =>
            savedCandidateIds.has(candidate.candidatePersonId)
                ? {
                      ...candidate,
                      present: PresentCandidateValues.YES,
                  }
                : candidate,
        )

        const newlySavedFromEphemeral: CandidateLiteDto[] = []
        const remainingEphemeralCandidates: CandidateLiteDto[] = []

        for (const candidate of ephemeralCandidates) {
            if (savedCandidateIds.has(candidate.candidatePersonId)) {
                newlySavedFromEphemeral.push({
                    ...candidate,
                    present: PresentCandidateValues.YES,
                })
            } else {
                remainingEphemeralCandidates.push(candidate)
            }
        }

        const nextStoreCandidates = [
            ...updatedStoreCandidates,
            ...newlySavedFromEphemeral,
        ]

        setIsSavingAllCandidates(true)

        try {
            await roleApi.setCandidatesPresentValue({
                roleId,
                candidateIds,
                presentCandidateValue: PresentCandidateValues.YES,
            })

            setStoreCandidates(nextStoreCandidates)
            setEphemeralCandidates(remainingEphemeralCandidates)

            setHelixCandidateLiteDtos((prev) =>
                prev.map((candidate) =>
                    savedCandidateIds.has(candidate.candidatePersonId)
                        ? {
                              ...candidate,
                              present: PresentCandidateValues.YES,
                          }
                        : candidate,
                ),
            )

            toast.success(
                `Saved ${candidateIds.length} candidate${
                    candidateIds.length === 1 ? '' : 's'
                } to the role.`,
            )
        } catch (error) {
            console.error('Failed to save candidates to role', error)
            toast.error('Failed to save candidates. Please try again.')
        } finally {
            setIsSavingAllCandidates(false)
        }
    }, [
        roleId,
        helixCandidateLiteDtos,
        setStoreCandidates,
        setEphemeralCandidates,
        manageCandidateStore,
    ])

    const rightPaneContent = useMemo(() => {
        switch (rightPaneMode) {
            case 'search_filters':
                return (
                    <SearchFilters
                        networkSeedPersons={networkLinksSelectedInNetworkLinks}
                        roleId={roleId}
                        roleJobDescription={roleLite?.jobDescription}
                        onOpenTrustedCircleModal={handleAddPeopleClick}
                        openModalSignal={filtersModalOpenSignal}
                        savedFilters={filters}
                        onFiltersChange={handleFiltersChange}
                        onSearch={handleFiltersSearch}
                        onSearchWithAi={handleFiltersSearchWithAi}
                        onSaveFilters={handleSaveFilters}
                        experienceMinYears={minExperienceYears}
                        experienceMaxYears={maxExperienceYears}
                        defaultMinExperienceYears={DEFAULT_MIN_EXPERIENCE_YEARS}
                        defaultMaxExperienceYears={DEFAULT_MAX_EXPERIENCE_YEARS}
                        onExperienceMinYearsChange={
                            handleMinExperienceYearsChange
                        }
                        onExperienceMaxYearsChange={
                            handleMaxExperienceYearsChange
                        }
                        isSavingFilters={isSavingFilters}
                        filtersModalError={filtersModalSearchError}
                        onFiltersModalClose={() =>
                            setFiltersModalSearchError(null)
                        }
                        networkLinksPersonLiteCache={personLiteCacheRef.current}
                    />
                )
            case 'boolean_search_help':
                return <HelixBooleanSearchHelp />
            case 'saved_searches':
                return <div>saved searches</div>
            case 'candidate':
                if (!selectedCandidateId) {
                    return null
                }
                return (
                    <CandidateRightPane
                        candidateId={selectedCandidateId}
                        networkLinksPersonLiteCache={personLiteCacheRef.current}
                        candidatesPersonCache={personCacheRef.current}
                        companyCache={companyCacheRef.current}
                        candidateConnections={selectedCandidateConnections}
                        roleLite={roleLite}
                    />
                )
            case 'network_seed':
                return <div>NetworkSeedRightPane</div>
            case 'company':
                return <div>CompanyRightPane</div>
            default:
                throw new Error(`Invalid mode: ${rightPaneMode}`)
        }
    }, [
        rightPaneMode,
        networkLinksSelectedInNetworkLinks,
        roleId,
        roleLite,
        handleAddPeopleClick,
        filtersModalOpenSignal,
        filters,
        handleFiltersChange,
        handleFiltersSearch,
        handleFiltersSearchWithAi,
        handleSaveFilters,
        minExperienceYears,
        maxExperienceYears,
        handleMinExperienceYearsChange,
        handleMaxExperienceYearsChange,
        isSavingFilters,
        filtersModalSearchError,
        selectedCandidateId,
        selectedCandidateConnections,
    ])

    const hasSavedCandidatesForRole =
        savedCandidateCount > 0 ||
        (!roleCandidatesLoaded && (initialSavedCandidateCount ?? 0) > 0)
    const shouldShowBackToRoleLink =
        Boolean(roleId) && hasSavedCandidatesForRole

    return (
        <>
            <LayoutSplitView
                activeMenuItem={
                    roleId ? MenuItems.Roles : MenuItems.HelixSearch
                }
                className=""
                rightPane={rightPaneContent}
            >
                <div className="flex items-center justify-end mb-4 gap-2 flex-wrap">
                    {roleId && (
                        <>
                            <CtxtLink
                                to={`${Pages.ManageRoleTA}?id=${roleId}`}
                                className={
                                    shouldShowBackToRoleLink ? '' : 'invisible'
                                }
                            >
                                View Saved Candidates
                            </CtxtLink>
                            <Button
                                style="secondary"
                                type="link"
                                to={`${Pages.UpdateRole}?id=${roleId}&returnTo=${encodedReturnToEditRole}`}
                                classes=""
                            >
                                <FontAwesomeIcon
                                    icon={faPencil}
                                    className={`mr-2`}
                                />
                                Edit Role
                            </Button>
                        </>
                    )}
                    <Button
                        style="secondary"
                        type="button"
                        onClick={handleOpenSearchFiltersPane}
                        enabled={rightPaneMode !== 'search_filters'}
                    >
                        <FontAwesomeIcon icon={faFilter} className="mr-2" />
                        {rightPaneMode === 'search_filters'
                            ? 'Filters Open'
                            : 'Open Search Filters'}
                    </Button>
                </div>
                {roleId && roleLite && (
                    <div className="mb-2">
                        <RoleCard
                            role={roleLite}
                            clickable={false}
                            showJobDescription={true}
                        />
                    </div>
                    // <RoleDetails
                    //     roleId={roleId}
                    //     roleLite={roleLite}
                    //     personLiteCache={personLiteCacheRef.current}
                    // />
                )}
                {/* <TrustedCircle
                    networkLinks={networkLinksSelectedInNetworkLinks}
                    hiringManagerIds={
                        roleLite?.hiringTeamIds ?? EMPTY_STRING_ARRAY
                    }
                    trustedCircleError={trustedCircleError}
                    onAddPeopleClick={handleAddPeopleClick}
                    onRemoveNetworkLink={handleRemoveNetworkLink}
                />

                <BooleanSearch
                    query={query}
                    booleanQueryError={booleanQueryError}
                    onQueryChange={handleBooleanQueryChange}
                    onSearchClick={handleSearchClick}
                    hasRole={Boolean(roleId)}
                    experienceMinYears={minExperienceYears}
                    experienceMaxYears={maxExperienceYears}
                    onExperienceMinYearsChange={
                        handleMinExperienceYearsChange
                    }
                    onExperienceMaxYearsChange={
                        handleMaxExperienceYearsChange
                    }
                    enabledLLMScoring={enabledLLMScoring}
                    onEnableLlmScoringChange={setEnabledLLMScoring}
                    onShowHelp={handleShowHelp}
                    networkImportModalState={networkImportModalState}
                    onNetworkImportComplete={handleNetworkImportComplete}
                    onNetworkImportCancel={handleNetworkImportCancel}
                    showHelixV2ProgressModal={showHelixV2ProgressModal}
                    helixV2SearchSummary={helixV2SearchSummary}
                    onProgressModalClose={handleHelixProgressModalClose}
                    progressModalAutoCloseDeadline={
                        progressModalAutoCloseDeadline
                    }
                    showResults={showResults}
                /> */}
                {errorMsg && (
                    <Note type={NoteType.ERROR} containerClassNames="bg-white">
                        {errorMsg}
                    </Note>
                )}
                {showResults && !errorMsg && (
                    <div>
                        <div
                            className="bg-white rounded-2xl shadow-md p-6 mb-6"
                            ref={insightsRef}
                        >
                            <div className="flex items-center gap-2 text-lg font-semibold text-gray-800 pb-2 mb-4">
                                <FontAwesomeIcon
                                    icon={faChartBar}
                                    className="text-ctxt-blue"
                                />
                                Search Insights
                            </div>
                            <HelixSearchV2Insights
                                inNetwork={
                                    helixV2StatusDetails?.networkPersonCount ??
                                    0
                                }
                                matchesBoth={
                                    helixV2StatusDetails?.booleanSearchPersonCount ??
                                    0
                                }
                                filteredCandidatePoolCount={
                                    filteredCandidatePoolCount
                                }
                                topPickCount={
                                    helixV2StatusDetails?.topPickCount
                                }
                                filteredTopPickCount={filteredTopPickCount}
                                enabledLlmScoring={
                                    helixV2StatusDetails?.enableLlmScoring ??
                                    false
                                }
                                logStatus={
                                    helixV2StatusDetails?.logStatus ?? null
                                }
                                size="compact"
                            />
                        </div>
                        <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
                            <div className="flex items-center gap-2 text-lg font-semibold text-gray-800 pb-2 mb-4">
                                <FontAwesomeIcon
                                    icon={faFilter}
                                    className="text-ctxt-blue"
                                />
                                Filters
                            </div>
                            <HelixSearchV2Filters
                                roleId={roleId}
                                enableLlmScoring={resultsEnableLlmScoring}
                                availableNetworkLinkIds={
                                    networkLinkIdsSetInThisSearch
                                }
                                personLiteCache={personLiteCacheRef.current}
                                applyClick={handleFilterApplyClick}
                                resetClick={handleFilterResetClick}
                                networkLinksSelected={
                                    networkLinksSelectedInFilters
                                }
                                shortlistedSelected={shortlistedSelected}
                                minRelationshipScore={minRelationshipScore}
                                orderBy={orderBy}
                                filteredCandidatePoolCount={
                                    filteredCandidatePoolCount
                                }
                                filteredCandidatePoolCountIsLoading={
                                    filteredCandidatePoolCountIsLoading
                                }
                                networkLinksSelectedChanged={
                                    networkLinksSelectedChanged
                                }
                                shortlistedSelectedChanged={
                                    shortlistedSelectedChanged
                                }
                                minRelationshipScoreChanged={
                                    minRelationshipScoreChanged
                                }
                                orderByChanged={orderByChanged}
                                mustBeInBooleanSearchResultsChanged={
                                    mustBeInBooleanSearchResultsChanged
                                }
                                mustHaveRelationshipChanged={
                                    mustHaveRelationshipChanged
                                }
                                filtersAreDirty={filtersAreDirty}
                            />
                        </div>
                        {searchResults.length > 0 ? (
                            <>
                                <div
                                    ref={networkViewRef}
                                    className="bg-white rounded-2xl shadow-md p-6 mb-6"
                                >
                                    <div className="flex items-center gap-2 text-lg font-semibold text-gray-800 pb-2 mb-4">
                                        <FontAwesomeIcon
                                            icon={faHexagonNodes}
                                            className="text-ctxt-blue"
                                        />
                                        Candidate Network
                                    </div>
                                    <HelixSearchNetworkView
                                        resolvedPersons={resolvedPersonLites}
                                        networkLinksPersonCache={
                                            personLiteCacheRef.current
                                        }
                                        candidatesPersonCache={
                                            personCacheRef.current
                                        }
                                        companyCache={companyCacheRef.current}
                                        candidatePresentById={
                                            candidatePresentById
                                        }
                                        onCandidateSelection={(candidateId) => {
                                            setSelectedCandidateId(candidateId)
                                            setRightPaneMode('candidate')
                                        }}
                                        hasRole={roleLite !== null}
                                    />
                                </div>
                                <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
                                    <CandidateList
                                        roleId={roleId}
                                        candidates={helixCandidateLiteDtos}
                                        personCache={personCacheRef.current}
                                        personLiteCache={
                                            personLiteCacheRef.current
                                        }
                                        resolvedPersonLites={
                                            resolvedPersonLites
                                        }
                                        companyCache={companyCacheRef.current}
                                        currentPageNumber={currentPageNumber}
                                        perPage={perPage}
                                        totalResultCount={totalResultCount}
                                        pageChanged={pageChanged}
                                        perPageChanged={perPageChanged}
                                        getCandidateRank={getCandidateRank}
                                        onCandidateSelection={
                                            handleCandidateSelection
                                        }
                                        onSaveAllCandidates={
                                            handleSaveAllCandidatesToRole
                                        }
                                        isSavingAllCandidates={
                                            isSavingAllCandidates
                                        }
                                    />
                                </div>
                            </>
                        ) : (
                            <Note
                                type={NoteType.WARNING}
                                containerClassNames="bg-white mb-16"
                            >
                                No results. Try refining the filters or the
                                search criteria.
                            </Note>
                        )}
                    </div>
                )}
                {isModalOpen && (
                    <AddNetworkLinksModal
                        currentNetworkLinksIds={
                            networkLinksIdsSelectedInNetworkLinks
                        }
                        onClose={addNetworkLinksModalClosed}
                        onLiveSelectChange={handleLiveNetworkLinkSelection}
                        onAddFromDepartments={addNetworkLinksFromDepartments}
                    />
                )}
            </LayoutSplitView>
            {showDataLoading && (
                <HelixSearchDataLoadingModal onClose={handleDataLoadingClose} />
            )}
            {networkImportModalState && (
                <PersonNetworkImportModal
                    isOpen={Boolean(networkImportModalState)}
                    personIds={networkImportModalState.personIds}
                    personNamesById={networkImportModalState.personNamesById}
                    personPhotoUrlsById={
                        networkImportModalState.personPhotoUrlsById
                    }
                    onComplete={handleNetworkImportComplete}
                    onCancel={handleNetworkImportCancel}
                />
            )}
            {showHelixV2ProgressModal && helixV2SearchSummary && (
                <HelixSearchV2ProgressModalCopy
                    sst={helixV2SearchSummary.sst}
                    logId={helixV2SearchSummary.logId}
                    onClose={handleHelixProgressModalClose}
                    autoCloseDeadline={progressModalAutoCloseDeadline}
                    resultsLoaded={showResults}
                />
            )}
            {roleId && (
                <Button
                    style="fab"
                    type="link"
                    to={`${Pages.ManageRoleTA}?id=${roleId}`}
                    classes={`fixed bottom-6 left-1/2 -translate-x-1/2 z-30 ${
                        shouldShowBackToRoleLink ? '' : 'invisible'
                    }`}
                >
                    <FontAwesomeIcon icon={faBookmark} className="mr-2" />
                    View Saved Candidates
                </Button>
            )}
        </>
    )
}
